// Métricas realistas basadas en WhatsApp Business API y Webhooks
// Documentación: https://developers.facebook.com/docs/whatsapp/cloud-api/webhooks

export const generateWhatsAppBusinessMetrics = (
  campaigns,
  contacts,
  lists,
  organizations
) => {
  const now = new Date();
  const last30Days = Array.from({length: 30}, (_, i) => {
    const date = new Date(now);
    date.setDate(date.getDate() - (29 - i));
    return date;
  });

  return {
    // Métricas principales basadas en WhatsApp Business API
    overview: {
      // Datos básicos de la aplicación
      totalCampaigns: campaigns.length,
      activeCampaigns: campaigns.filter((c) => c.status === "Enviada").length,
      totalContacts: contacts.length,
      totalOrganizations: organizations.length,
      totalLists: lists.length,
      messagesThisMonth: Math.floor(Math.random() * 3000 + 8000), // Volumen realista para PyME

      // Estados de mensajes según WhatsApp Business API Webhooks
      // https://developers.facebook.com/docs/whatsapp/cloud-api/webhooks/components#statuses-object
      sentMessages: Math.floor(Math.random() * 500 + 2800), // Mensajes enviados
      deliveredMessages: Math.floor(Math.random() * 450 + 2650), // Status: delivered
      readMessages: Math.floor(Math.random() * 350 + 2100), // Status: read
      failedMessages: Math.floor(Math.random() * 50 + 25), // Status: failed

      // Tasas calculadas basadas en estados reales
      deliveredRate: 96.2, // delivered/sent * 100
      readRate: 78.8, // read/delivered * 100
      responseRate: 11.7, // Respuestas recibidas/enviados * 100
      failedRate: 3.8, // failed/sent * 100

      // Métricas de tiempo basadas en timestamps de webhooks
      avgDeliveryTime: "1.8 min", // Tiempo sent -> delivered
      avgReadTime: "12.4 min", // Tiempo delivered -> read
      avgResponseTime: "3.2 horas", // Tiempo read -> respuesta del usuario

      // Costos reales de WhatsApp Business API (2024)
      // https://developers.facebook.com/docs/whatsapp/pricing
      costPerMessage: 0.0465, // USD por mensaje (conversación iniciada por negocio)
      costPerUserInitiated: 0.0093, // USD por mensaje (conversación iniciada por usuario)
      monthlySpend: Math.floor(Math.random() * 200 + 350), // Gasto mensual total

      // Template Messages (Plantillas aprobadas por WhatsApp)
      approvedTemplates: 12,
      pendingTemplates: 2,
      rejectedTemplates: 1,
      pausedTemplates: 0,

      // Límites y cuotas de la API
      messagingLimit: 1000, // Límite de mensajes por día (tier inicial)
      messagesUsedToday: Math.floor(Math.random() * 300 + 450),
      rateLimitRemaining: Math.floor(Math.random() * 200 + 300),

      // Calidad del número de teléfono (Phone Number Quality Rating)
      // https://developers.facebook.com/docs/whatsapp/cloud-api/phone-numbers
      phoneNumberQuality: "HIGH", // HIGH, MEDIUM, LOW, FLAGGED
      qualityScore: 4.2, // Score de 1-5
      displayName: "Conecta360",
      verificationStatus: "VERIFIED", // VERIFIED, UNVERIFIED, PENDING

      // Métricas de conversaciones (Conversation-based pricing)
      businessInitiatedConversations: Math.floor(Math.random() * 200 + 800),
      userInitiatedConversations: Math.floor(Math.random() * 150 + 600),
      freeConversations: Math.floor(Math.random() * 50 + 100), // Primeras 1000 gratis

      // Webhook delivery status
      webhookDeliveryRate: 99.2, // Tasa de entrega exitosa de webhooks
      webhookErrors: 3, // Errores en webhooks último mes

      // Media messages (imágenes, documentos, etc.)
      mediaMessagesCount: Math.floor(Math.random() * 100 + 250),
      mediaDeliveryRate: 94.5, // Menor que texto por tamaño

      // Opt-ins y opt-outs
      optIns: Math.floor(Math.random() * 50 + 120),
      optOuts: Math.floor(Math.random() * 20 + 15),
      blockedUsers: Math.floor(Math.random() * 10 + 5),
    },

    // Tendencias realistas para WhatsApp Business
    trends: {
      campaigns: 8.2, // Crecimiento más conservador
      contacts: 12.4,
      deliveredRate: 1.8,
      readRate: -2.1, // Puede bajar por saturación de usuarios
      responseRate: 5.7,
      messageVolume: 15.3,
      costs: -3.2, // Optimización de costos
      qualityScore: 0.3, // Mejora gradual
    },

    // Datos diarios con métricas realistas de WhatsApp
    dailyData: last30Days.map((date) => {
      const baseMessages = Math.floor(Math.random() * 200 + 150);
      const delivered = Math.floor(
        baseMessages * (0.95 + Math.random() * 0.04)
      ); // 95-99%
      const read = Math.floor(delivered * (0.75 + Math.random() * 0.15)); // 75-90%
      const responded = Math.floor(read * (0.08 + Math.random() * 0.08)); // 8-16%
      const failed = baseMessages - delivered;

      return {
        date: date.toISOString().split("T")[0],
        sent: baseMessages,
        delivered,
        read,
        responded,
        failed,
        // Métricas específicas de WhatsApp Business
        templateMessages: Math.floor(baseMessages * 0.7), // 70% son template messages
        sessionMessages: Math.floor(baseMessages * 0.3), // 30% son mensajes de sesión
        mediaMessages: Math.floor(baseMessages * 0.15), // 15% incluyen media
        businessInitiated: Math.floor(baseMessages * 0.6), // 60% iniciadas por negocio
        userInitiated: Math.floor(baseMessages * 0.4), // 40% iniciadas por usuario
        cost: (baseMessages * 0.0465).toFixed(2), // Costo diario
      };
    }),

    // Estados de mensajes según WhatsApp API
    messageStatuses: {
      sent: {count: 2850, percentage: 100},
      delivered: {count: 2742, percentage: 96.2},
      read: {count: 2161, percentage: 78.8},
      failed: {count: 108, percentage: 3.8},
      // Estados específicos de fallo
      failureReasons: {
        invalid_phone_number: 45,
        rate_limit_hit: 23,
        insufficient_balance: 12,
        template_not_approved: 18,
        user_blocked_business: 10,
      },
    },

    // Análisis de plantillas (Template Messages)
    templateAnalysis: campaigns.slice(0, 5).map((campaign) => ({
      id: campaign.id,
      name: campaign.name,
      templateName: `template_${campaign.name
        .toLowerCase()
        .replace(/\s+/g, "_")}`,
      status: Math.random() > 0.8 ? "PENDING" : "APPROVED",
      category: Math.random() > 0.5 ? "MARKETING" : "UTILITY",
      language: "es",
      sent: Math.floor(Math.random() * 500 + 200),
      delivered: Math.floor(Math.random() * 450 + 180),
      read: Math.floor(Math.random() * 350 + 140),
      clicked: Math.floor(Math.random() * 50 + 20), // Para templates con botones
      cost: (Math.random() * 20 + 10).toFixed(2),
      qualityRating: (4 + Math.random()).toFixed(1),
    })),

    // Análisis de horarios óptimos basado en read receipts
    hourlyActivity: Array.from({length: 24}, (_, hour) => {
      // Patrones realistas de uso de WhatsApp en Chile
      let baseActivity = 20;
      if (hour >= 9 && hour <= 12) baseActivity = 80; // Mañana alta
      if (hour >= 14 && hour <= 18) baseActivity = 70; // Tarde media-alta
      if (hour >= 19 && hour <= 22) baseActivity = 90; // Noche muy alta
      if (hour >= 23 || hour <= 6) baseActivity = 10; // Madrugada baja

      return {
        hour,
        sent: Math.floor(baseActivity + Math.random() * 20),
        delivered: Math.floor((baseActivity + Math.random() * 20) * 0.96),
        read: Math.floor((baseActivity + Math.random() * 20) * 0.78),
        responded: Math.floor((baseActivity + Math.random() * 20) * 0.12),
        avgReadTime: Math.floor(Math.random() * 30 + 5), // minutos
      };
    }),

    // Alertas específicas de WhatsApp Business
    whatsappAlerts: [
      {
        id: 1,
        type: "warning",
        title: "Límite diario próximo",
        message: `Has usado ${Math.floor(
          Math.random() * 100 + 850
        )} de 1000 mensajes diarios`,
        time: "10 min ago",
        action: "Ver límites",
        severity: "medium",
      },
      {
        id: 2,
        type: "error",
        title: "Template rechazada",
        message: 'La plantilla "promo_navidad" fue rechazada por WhatsApp',
        time: "2 horas ago",
        action: "Revisar template",
        severity: "high",
      },
      {
        id: 3,
        type: "success",
        title: "Calidad mejorada",
        message: "Tu número de teléfono mantiene calidad HIGH",
        time: "1 día ago",
        action: "Ver métricas",
        severity: "low",
      },
      {
        id: 4,
        type: "info",
        title: "Webhook configurado",
        message: "Webhook de estados configurado correctamente",
        time: "3 días ago",
        action: "Ver configuración",
        severity: "low",
      },
    ],

    // Recomendaciones específicas para WhatsApp Business
    whatsappRecommendations: [
      {
        id: 1,
        type: "template",
        title: "Optimizar plantillas",
        description: "Crear templates con botones para mayor interacción",
        priority: "high",
        estimatedImpact: "+25% tasa de respuesta",
        action: "crear_template",
      },
      {
        id: 2,
        type: "timing",
        title: "Ajustar horarios de envío",
        description: "Enviar entre 19:00-22:00 para mejor lectura",
        priority: "medium",
        estimatedImpact: "+12% tasa de lectura",
        action: "programar_envios",
      },
      {
        id: 3,
        type: "cost",
        title: "Optimizar conversaciones",
        description: "Agrupar mensajes en ventana de 24h para reducir costos",
        priority: "medium",
        estimatedImpact: "-20% costos mensuales",
        action: "optimizar_costos",
      },
      {
        id: 4,
        type: "quality",
        title: "Mantener calidad",
        description: "Evitar spam y mensajes masivos para mantener rating HIGH",
        priority: "high",
        estimatedImpact: "Mantener deliverability",
        action: "revisar_politicas",
      },
    ],

    // Métricas de engagement específicas de WhatsApp
    engagement: {
      // Basado en interacciones reales de WhatsApp
      messageReplies: Math.floor(Math.random() * 200 + 350), // Respuestas de texto
      buttonClicks: Math.floor(Math.random() * 100 + 150), // Clicks en botones de templates
      listReplies: Math.floor(Math.random() * 50 + 80), // Respuestas a listas interactivas
      quickReplies: Math.floor(Math.random() * 80 + 120), // Respuestas rápidas
      mediaShares: Math.floor(Math.random() * 30 + 45), // Compartir media
      contactShares: Math.floor(Math.random() * 20 + 25), // Compartir contactos
      locationShares: Math.floor(Math.random() * 15 + 20), // Compartir ubicación

      // Métricas de sesión (24h window)
      activeConversations: Math.floor(Math.random() * 100 + 200),
      avgMessagesPerConversation: (2.3 + Math.random() * 1.5).toFixed(1),
      conversationDuration: "18.5 min", // Duración promedio de conversación

      // Opt-out rate (importante para WhatsApp compliance)
      optOutRate: (1.2 + Math.random() * 0.8).toFixed(1) + "%", // Debe ser < 2%
      blockRate: (0.3 + Math.random() * 0.4).toFixed(1) + "%", // Debe ser < 1%
      spamReports: Math.floor(Math.random() * 3 + 1), // Debe ser mínimo
    },
    topOrganizations: (organizations || []).slice(0, 5).map((org, index) => ({
      ...org,
      engagement: (85 - index * 5 + Math.random() * 10).toFixed(1),
      messages: Math.floor(Math.random() * 500 + 100),
      responseRate: (20 + Math.random() * 15).toFixed(1),
      lastActivity: new Date(
        Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000
      ),
    })),
    recentCampaigns: (campaigns || []).slice(0, 5).map((campaign) => ({
      ...campaign,
      metrics: {sent: 0, read: 0, responded: 0},
    })),
  };
};

// Función para simular webhook de estado de mensaje
export const simulateWebhookStatus = (messageId) => {
  const statuses = ["sent", "delivered", "read", "failed"];
  const weights = [100, 96, 78, 4]; // Probabilidades realistas

  const random = Math.random() * 100;
  let cumulative = 0;

  for (let i = 0; i < statuses.length; i++) {
    cumulative += weights[i];
    if (random <= cumulative) {
      return {
        messageId,
        status: statuses[i],
        timestamp: new Date().toISOString(),
        recipientId: `whatsapp:+56${Math.floor(
          Math.random() * 900000000 + 100000000
        )}`,
        errors:
          statuses[i] === "failed"
            ? [
                {
                  code: Math.random() > 0.5 ? 131026 : 131047, // Códigos de error comunes
                  title:
                    Math.random() > 0.5
                      ? "Message undeliverable"
                      : "Re-engagement message",
                },
              ]
            : undefined,
      };
    }
  }
};

// Función para calcular métricas de calidad del número
export const calculatePhoneNumberQuality = (metrics) => {
  const {deliveredRate, optOutRate, spamReports, blockRate} = metrics;

  let score = 5.0;

  // Penalizar por baja entrega
  if (deliveredRate < 90) score -= 1.0;
  else if (deliveredRate < 95) score -= 0.5;

  // Penalizar por alta tasa de opt-out
  if (parseFloat(optOutRate) > 2) score -= 1.5;
  else if (parseFloat(optOutRate) > 1) score -= 0.5;

  // Penalizar por reportes de spam
  if (spamReports > 5) score -= 2.0;
  else if (spamReports > 2) score -= 1.0;

  // Penalizar por bloqueos
  if (parseFloat(blockRate) > 1) score -= 1.0;
  else if (parseFloat(blockRate) > 0.5) score -= 0.5;

  score = Math.max(1.0, Math.min(5.0, score));

  let quality = "HIGH";
  if (score < 3.0) quality = "LOW";
  else if (score < 4.0) quality = "MEDIUM";

  return {score: score.toFixed(1), quality};
};

// Límites reales de WhatsApp Business API por tier
export const getMessagingLimits = (tier = "TIER_1") => {
  const limits = {
    TIER_1: {daily: 1000, description: "Nuevo número de teléfono"},
    TIER_2: {daily: 10000, description: "Después de enviar 1000 mensajes"},
    TIER_3: {daily: 100000, description: "Después de enviar 10000 mensajes"},
    UNLIMITED: {
      daily: Infinity,
      description: "Números verificados de alto volumen",
    },
  };

  return limits[tier] || limits.TIER_1;
};

const whatsappBusinessMetrics = {
  generateWhatsAppBusinessMetrics,
  simulateWebhookStatus,
  calculatePhoneNumberQuality,
  getMessagingLimits,
};

export default whatsappBusinessMetrics;
